import React from 'react';
import {GridCellParams, GridColDef} from '@mui/x-data-grid-pro';
import {Box} from '@mui/system';
import moment from 'moment';
import {ColoredChip} from '../../components/EditorGrid/GridRenderComponents';
import {atrEcqStatusColors} from './constants/statusColors';
import {ReviewInvitationSummaryFragment} from '../../schema-types/ReviewInvitationSummaryFragment';
import {renderCellDateTime} from '../../utils/renderCellDateTime';
import {InviteButton, SendECQButton, SendRTQButton} from './InvitationActions';
import {gridCellDef} from '../../utils/GridCellDef';
import StatusPill from './components/StatusPill';
import {enumToLabel} from '../../utils/typescriptUtils';
import {renderCellDate} from '../../utils/renderCellDate';

// Helper function to detect if a row is a group row (month divider)
const isGroupRow = (row: any): boolean => {
  // In MUI DataGrid Pro tree data, group rows typically don't have the complete data structure
  // We can detect them by checking if key properties of ReviewInvitationSummaryFragment are missing
  return !row.account || !row.id || typeof row === 'string';
};
// Account name column (alternative field name)
export const accountNameColumn: GridColDef<ReviewInvitationSummaryFragment> = {
  field: 'accountName',
  headerName: 'Account',
  flex: 1.5,
  valueGetter: (params, row) => {
    if (isGroupRow(row)) {
      return '';
    }
    return row.account?.name || 'N/A';
  },
};

// Completed On column (for completed meetings)
export const completedOnColumn: GridColDef<ReviewInvitationSummaryFragment> = {
  field: 'completedOn',
  headerName: 'Completed On',
  flex: 1.2,
  valueGetter: (params, row) => {
    if (isGroupRow(row)) {
      return '';
    }
    return row.dateSubmitted;
  },
  renderCell: renderCellDateTime,
};

// Date declined column
export const dateDeclinedColumn: GridColDef<ReviewInvitationSummaryFragment> = {
  field: 'dateDeclined',
  headerName: 'Date Declined',
  flex: 1,
  valueGetter: (params, row) => {
    if (isGroupRow(row)) {
      return '';
    }
    return row.invitation?.dateUpdated;
  },
  renderCell: renderCellDateTime,
};

// Date uncontactable column
export const dateUncontactableColumn: GridColDef<ReviewInvitationSummaryFragment> = {
  field: 'dateUncontactable',
  headerName: 'Date Uncontactable',
  flex: 1,
  valueGetter: (params, row) => {
    if (isGroupRow(row)) {
      return '';
    }
    return row.invitation?.dateUpdated;
  },
  renderCell: renderCellDateTime,
};

// Sent on column
export const dateSentOnColumn: GridColDef<ReviewInvitationSummaryFragment> = {
  field: 'sentOn',
  headerName: 'Sent On',
  flex: 1,
  valueGetter: (params, row) => {
    if (isGroupRow(row)) {
      return '';
    }
    return row.invitation?.dateSent;
  },
  renderCell: renderCellDateTime,
};

export const renderCellDateWithDaysDifference: gridCellDef<unknown> = (params) => {
  if (isGroupRow(params.row)) {
    return null;
  }

  if (!params.value) {
    return (
      <Box flex={1} textAlign="center" alignContent="center" justifyContent="center">
        N/A
      </Box>
    );
  }

  const scheduledDate = moment(params.value?.valueOf()).startOf('day');
  const today = moment().startOf('day');
  const daysDifference = scheduledDate.diff(today, 'days');

  // Format the status text
  let statusText: string;
  if (daysDifference === 0) {
    statusText = 'Today';
  } else if (daysDifference === 1) {
    statusText = 'Tomorrow';
  } else if (daysDifference === -1) {
    statusText = 'Yesterday';
  } else if (daysDifference > 0) {
    statusText = `${daysDifference} days`;
  } else {
    statusText = `${Math.abs(daysDifference)} days ago`;
  }

  return (
    <Box flex={1} display="flex" alignItems="center" gap={1}>
      <Box>{scheduledDate.format('DD/MM/YY')}</Box>
      <StatusPill status={statusText} daysDifference={daysDifference} />
    </Box>
  );
};

// Render function for booked date and time in DD/MM/YY HH:MM format
export const renderCellBookedDateTime: gridCellDef<unknown> = (params) => {
  if (isGroupRow(params.row)) {
    return null;
  }

  if (!params.value) {
    return (
      <Box flex={1} textAlign="center" alignContent="center" justifyContent="center">
        N/A
      </Box>
    );
  }

  const bookedDate = moment(params.value?.valueOf());

  return (
    <Box flex={1} display="flex" alignItems="center">
      <Box>{bookedDate.format('DD/MM/YY HH:mm')}</Box>
    </Box>
  );
};

// Booked for column (date and time)
export const dateScheduledForColumn: GridColDef<ReviewInvitationSummaryFragment> = {
  field: 'bookedFor',
  headerName: 'Booked for',
  flex: 1,
  valueGetter: (params, row) => {
    if (isGroupRow(row)) {
      return '';
    }
    return row.invitation?.dateBooked;
  },
  renderCell: renderCellBookedDateTime,
};

// Scheduled for column (time only for today's meetings)
export const scheduledForTimeOnlyColumn: GridColDef<ReviewInvitationSummaryFragment> = {
  field: 'scheduledFor',
  headerName: 'Scheduled For',
  flex: 1.2,
  valueGetter: (params, row) => {
    if (isGroupRow(row)) {
      return '';
    }
    return row.dateScheduled;
  },
  renderCell: renderCellDateTime,
};

// Adviser column
export const adviserColumn: GridColDef<ReviewInvitationSummaryFragment> = {
  field: 'adviser',
  headerName: 'Adviser',
  flex: 1,
  valueGetter: (params, row) => {
    if (isGroupRow(row)) {
      return '';
    }
    return row.adviser?.name || 'N/A';
  },
};
// Owner column
export const ownerColumn: GridColDef<ReviewInvitationSummaryFragment> = {
  field: 'owner',
  headerName: 'Owner',
  flex: 1,
  valueGetter: (params, row) => {
    if (isGroupRow(row)) {
      return '';
    }
    return row?.owner?.name || 'N/A';
  },
};

// Meeting host column
export const meetingHostColumn: GridColDef<ReviewInvitationSummaryFragment> = {
  field: 'meetingHost',
  headerName: 'Meeting host',
  flex: 1,
  valueGetter: (params, row) => {
    if (isGroupRow(row)) {
      return '';
    }
    return row?.invitation?.invitationOwner?.name || 'N/A';
  },
};

// Sent by column
export const sentByColumn: GridColDef<ReviewInvitationSummaryFragment> = {
  field: 'sentBy',
  headerName: 'Sent By',
  flex: 1,
  valueGetter: (params, row) => {
    if (isGroupRow(row)) {
      return '';
    }
    return row.invitation?.sentByStaff?.name || 'N/A';
  },
};

// Location column (using meeting type)
export const locationColumn: GridColDef<ReviewInvitationSummaryFragment> = {
  field: 'location',
  headerName: 'Location',
  flex: 1,
  valueGetter: (params, row) =>
    isGroupRow(row)
      ? ''
      : row.invitation?.meetingType
      ? enumToLabel(row.invitation?.meetingType)
      : 'N/A',
};

// Reason declined column
export const reasonDeclinedColumn: GridColDef<ReviewInvitationSummaryFragment> = {
  field: 'reasonDeclined',
  headerName: 'Reason',
  flex: 1.5,
  valueGetter: (params, row) => {
    if (isGroupRow(row)) {
      return '';
    }
    return row.noAttendeeReason || 'N/A';
  },
};

// Reason uncontactable column
export const reasonUncontactableColumn: GridColDef<ReviewInvitationSummaryFragment> = {
  field: 'reasonUncontactable',
  headerName: 'Reason',
  flex: 2,
  valueGetter: (params, row) => {
    if (isGroupRow(row)) {
      return '';
    }
    return row.noAttendeeReason || 'N/A';
  },
};

// Contacts column
export const clientsColumn: GridColDef<ReviewInvitationSummaryFragment> = {
  field: 'clients',
  headerName: 'Clients',
  flex: 1.5,
  valueGetter: (params, row) => {
    if (isGroupRow(row)) {
      return '';
    }
    return row.contacts?.map((c) => `${c.simplifiedName}`).join(', ') || 'N/A';
  },
};

// Proposition type column
export const propositionTypeColumn: GridColDef<ReviewInvitationSummaryFragment> = {
  field: 'propositionType',
  headerName: 'Proposition',
  width: 120,
  headerAlign: 'center',
  align: 'center',
  valueGetter: (params, row) => {
    if (isGroupRow(row)) {
      return '';
    }
    return row.propositionType || 'N/A';
  },
};

const buttonWidth = 40;

const renderRTQ = (
  params: GridCellParams<ReviewInvitationSummaryFragment>,
  showAction: boolean
) => {
  if (isGroupRow(params.row)) {
    return null;
  }

  const atrStatus = params.row.atrStatus || 'Unknown';
  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        alignItems: 'center',
        justifyContent: showAction ? 'space-evenly' : 'center',
        gap: 0.5,
        height: '100%',
      }}
    >
      <Box sx={{flex: 1, display: 'flex', justifyContent: 'center'}}>
        <ColoredChip
          colorScheme={atrEcqStatusColors}
          // value={Math.random() > 0.5 ? atrStatus : 'woo'} // keep to test resizing and alignment
          value={atrStatus}
          size="small"
        />
      </Box>
      {showAction && (
        <Box
          sx={{flex: 0, width: buttonWidth, display: 'flex', justifyContent: 'center'}}
        >
          <SendRTQButton
            review={params.row}
            showDisabledButton={false}
            includeSendText={false}
            size="small"
            sx={{py: 0.5, mb: 0.5}} // horrible hack to make it optically vertically centered, being an arrow icon
          />
        </Box>
      )}
    </Box>
  );
};

// RTQ Status column function
export const rtqStatusColumn = (
  showAction: boolean
): GridColDef<ReviewInvitationSummaryFragment> => ({
  field: 'atrStatus',
  headerName: 'RTQ Status',
  flex: 1,
  headerAlign: 'center',
  align: 'center',
  renderCell: (params) => renderRTQ(params, showAction),
});

const renderECQ = (
  params: GridCellParams<ReviewInvitationSummaryFragment>,
  showAction: boolean
) => {
  if (isGroupRow(params.row)) {
    return null;
  }

  const ecqStatus = params.row.ecqStatus || 'Unknown';
  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        alignItems: 'center',
        justifyContent: showAction ? 'space-evenly' : 'center',
        gap: 0.5,
        height: '100%',
      }}
    >
      <Box sx={{flex: 1, display: 'flex', justifyContent: 'center'}}>
        <ColoredChip
          colorScheme={atrEcqStatusColors}
          // value={Math.random() > 0.5 ? ecqStatus : 'woo'} // keep to test resizing and alignment
          value={ecqStatus}
          size="small"
        />
      </Box>
      {showAction && (
        <Box
          sx={{flex: 0, width: buttonWidth, display: 'flex', justifyContent: 'center'}}
        >
          <SendECQButton
            review={params.row}
            showDisabledButton={false}
            includeSendText={false}
            size="small"
            sx={{py: 0.5, mb: 0.5}} // horrible hack to make it optically vertically centered, being an arrow icon
          />
        </Box>
      )}
    </Box>
  );
};

// ECQ Status column function
export const ecqStatusColumn = (
  showAction: boolean
): GridColDef<ReviewInvitationSummaryFragment> => ({
  field: 'ecqStatus',
  headerName: 'ECQ Status',
  headerAlign: 'center',
  flex: 1,
  renderCell: (params) => renderECQ(params, showAction),
  align: 'center',
});

const renderInvite = (params: GridCellParams<ReviewInvitationSummaryFragment>) => {
  if (isGroupRow(params.row)) {
    return null;
  }

  const review = params.row;

  return (
    <Box sx={{display: 'flex', justifyContent: 'center'}}>
      <InviteButton showDisabledButton={false} review={review} includeSendText={false} />
    </Box>
  );
};

// Invite column
export const inviteColumn: GridColDef<ReviewInvitationSummaryFragment> = {
  field: 'invite',
  headerName: 'Invite',
  flex: 1,
  headerAlign: 'center',
  align: 'center',
  sortable: false,
  filterable: false,
  disableColumnMenu: true,
  renderCell: renderInvite,
};

// Review Due In column (for To Invite page) - using dateScheduled as placeholder
export const reviewDueInColumn: GridColDef<ReviewInvitationSummaryFragment> = {
  field: 'reviewDueIn',
  align: 'center',
  headerAlign: 'center',
  headerName: 'Due In',
  flex: 0.8,
  valueGetter: (params, row) => {
    if (isGroupRow(row)) {
      return null;
    }

    // Calculate days until scheduled date (using dateScheduled as proxy for due date)
    if (row.dateScheduled) {
      const dueDate = moment(row.dateScheduled).startOf('day');
      const today = moment().startOf('day');
      const daysDifference = dueDate.diff(today, 'days');

      if (daysDifference === 0) return 'Today';
      if (daysDifference === 1) return 'Tomorrow';
      if (daysDifference === -1) return 'Yesterday';
      if (daysDifference > 0) return `${daysDifference} days`;
      return `${Math.abs(daysDifference)} days ago`;
    }
    return 'N/A';
  },
};

// Review Due Date column (for To Invite page) - using dateScheduled as placeholder
export const reviewDueDateColumn: GridColDef<ReviewInvitationSummaryFragment> = {
  field: 'reviewDueDate',
  headerName: 'Due Date',
  align: 'center',
  headerAlign: 'center',
  flex: 0.8,
  valueGetter: (params, row) => {
    if (isGroupRow(row)) {
      return null;
    }
    return row.dateScheduled;
  },
  renderCell: (params) => {
    if (isGroupRow(params.row)) {
      return null;
    }
    return renderCellDate(params);
  },
};
